import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Repeat,
  Info,
  CheckCircle,
  XCircle,
} from "lucide-react";

const PalindromeNumberVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Even Digits Palindrome",
      n: 4554,
    },
    {
      id: 2,
      name: "Odd Digits Palindrome",
      n: 121,
    },
    {
      id: 3,
      name: "Not a Palindrome",
      n: 7789,
    },
    {
      id: 4,
      name: "Negative Number",
      n: -121,
    },
    {
      id: 5,
      name: "Ends with Zero",
      n: 10,
    },
    {
      id: 6,
      name: "Single Digit",
      n: 5,
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [n, setN] = useState(defaultTestCases[0].n);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function isPalindrome(n):", indent: 0 },
    { line: "if n < 0: return false", indent: 1 },
    { line: "original = n, reversed = 0", indent: 1 },
    { line: "", indent: 0 },
    { line: "while n > 0:", indent: 1 },
    { line: "digit = n % 10", indent: 2 },
    { line: "reversed = (reversed * 10) + digit", indent: 2 },
    { line: "n = floor(n / 10)", indent: 2 },
    { line: "", indent: 0 },
    { line: "return original == reversed", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (num) => {
    const allSteps = [];
    let stepCount = 0;
    let result = null;
    let currentN = num;
    let reversed = 0;
    const original = num;

    const addStep = (description, highlightLine, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        original,
        currentN,
        reversed,
        result,
        ...details,
      });
    };

    addStep("Start with the original number.", 0);

    if (num < 0) {
      addStep("Negative numbers are not palindromes.", 1, {
        isFinal: true,
        result: false,
      });
      return allSteps;
    }

    addStep("Initialize variables: original and reversed.", 2);

    while (currentN > 0) {
      addStep("Start of loop.", 4);

      const digit = currentN % 10;
      addStep(`Extract the last digit: ${digit}.`, 5, { digit });

      reversed = reversed * 10 + digit;
      addStep(
        `Append digit to reversed number. Reversed is now ${reversed}.`,
        6,
        { digit }
      );

      currentN = Math.floor(currentN / 10);
      addStep(`Remove last digit from number. Number is now ${currentN}.`, 7, {
        digit,
      });
    }

    addStep("Number is 0, loop finishes. Compare original and reversed.", 9);

    result = original === reversed;
    addStep(`Comparison: ${original} == ${reversed} is ${result}.`, 9, {
      isFinal: true,
      result,
    });

    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(n);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [n]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setN(testCases[index].n);
  };

  const addCustomTestCase = () => {
    try {
      const num = parseInt(customInput);
      if (isNaN(num)) throw new Error("Input must be a valid integer.");
      const newTestCase = { id: Date.now(), name: `Custom`, n: num };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid input: ${error.message}`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Repeat className="text-blue-500" /> Check if a Number is a Palindrome
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an integer `N`, return `true` if it is a palindrome, else
                return `false`. A palindrome is a number that reads the same
                backward as forward.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 font-mono">
                      N = {tc.n}
                    </div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="number"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom N: 12321"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-8 grid grid-cols-2 gap-4 text-center">
              <div>
                <h4 className="text-lg font-semibold mb-2">Number</h4>
                <div className="p-4 bg-gray-100 rounded-lg text-4xl font-mono relative">
                  {currentStepData?.currentN}
                  {currentStepData?.digit !== undefined && (
                    <span className="absolute -right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-yellow-300 text-yellow-800 animate-ping-once">
                      {currentStepData.digit}
                    </span>
                  )}
                  {currentStepData?.digit !== undefined && (
                    <span className="absolute -right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-yellow-300 text-yellow-800">
                      {currentStepData.digit}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-2">Reversed</h4>
                <div className="p-4 bg-gray-100 rounded-lg text-4xl font-mono">
                  {currentStepData?.reversed}
                </div>
              </div>
            </div>

            {currentStepData?.isFinal && (
              <div
                className={`mt-4 p-3 rounded-lg text-center ${
                  currentStepData.result ? "bg-green-100" : "bg-red-100"
                }`}
              >
                <div
                  className={`flex items-center justify-center font-bold ${
                    currentStepData.result ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {currentStepData.result ? (
                    <CheckCircle className="mr-2" />
                  ) : (
                    <XCircle className="mr-2" />
                  )}
                  Result: {currentStepData.original} is{" "}
                  {currentStepData.result ? "a Palindrome" : "Not a Palindrome"}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The optimal approach avoids converting the number to a string. We
              can reverse the number mathematically. In a loop, we extract the
              last digit using the modulo operator (`% 10`), append it to our
              `reversed` number, and remove the last digit from the original
              number by integer division (`/ 10`). After the loop, we compare
              the original number with the reversed number.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(log10(N))
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N is the input number. Time complexity is proportional to the
              number of digits.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Negative numbers are generally not considered palindromes.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Numbers ending in 0 (except for 0 itself) cannot be
                  palindromes because the reversed number would not start with
                  0. The standard algorithm handles this naturally.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  An optimization is to only reverse half the number. When the
                  original number becomes less than or equal to the reversed
                  number, the loop can stop.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-yellow-300 flex items-center justify-center font-bold">
                  5
                </div>
                <span>Digit being moved</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PalindromeNumberVisualizer;
